import React, { useEffect, useRef, useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

/** How long the pointer rests on a "Note" tag before its note shows, so passing over the list does not flash every note. */
const NOTE_CARD_DELAY_MS = 200;

interface PlaybookItemNoteProps {
  note: string;
}

/**
 * A note the service just added from the customer's response. It slides in on
 * green, then folds away once LIVE_NOTE_SHOWN_MS is up (`.note-arrive`).
 * Notes otherwise stay out of the list so it does not grow as the call goes on
 * (owner, 2026-10-23): the item's "Note" tag shows its note on hover and edits
 * it on a click. Same pattern as macOS and WPF.
 */
const PlaybookItemNote: React.FC<PlaybookItemNoteProps> = ({ note }) => (
  <div role="status" className="note-arrive mt-1 -mx-1 rounded-inner px-1 text-[11px] text-ink-secondary">
    <div className="whitespace-pre-wrap break-words">
      <span className="font-medium text-ink-muted">Note: </span>
      {note}
    </div>
  </div>
);

interface PlaybookNoteButtonProps {
  onClick: () => void;
}

/**
 * The pencil right after a checked item's text (18 × 18, glyph 11) that adds a
 * note. It shows on hover or keyboard focus, so it takes no room. An item that
 * has a note shows its "Note" tag instead.
 */
export const PlaybookNoteButton: React.FC<PlaybookNoteButtonProps> = ({ onClick }) => (
  <button
    type="button"
    aria-label="Add note"
    title="Add note"
    onClick={onClick}
    className="-my-0.5 ml-1 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-inner align-middle text-ink-muted opacity-0 hover:bg-surface-subtle hover:text-ink group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-signature"
  >
    <EditOutlinedIcon sx={{ fontSize: 11 }} />
  </button>
);

/**
 * A saved note, shown over the list while its "Note" tag is hovered or
 * focused, so the note takes no room otherwise (owner, 2026-10-23). It hangs
 * below the row (positioned against the row's `li`), under the item's text, at
 * most 320 wide. Same on macOS and WPF.
 */
const PlaybookNoteHoverCard: React.FC<{ note: string }> = ({ note }) => (
  <div
    role="tooltip"
    className="pointer-events-none absolute left-[26px] right-1 top-full z-20 -mt-0.5 max-w-[320px] animate-fade-in whitespace-pre-wrap break-words rounded-inner border border-hairline bg-surface px-2 py-1 text-[11px] text-ink-secondary shadow-md"
  >
    <span className="font-medium text-ink-muted">Note: </span>
    {note}
  </div>
);

interface PlaybookNoteTagProps {
  note: string;
  /** Opens the item's note editor. Absent while the checklist is read only: the tag still shows the note. */
  onEdit?: () => void;
}

/**
 * "Note", right after a checked item's text on its first line, for an item
 * with a saved note (owner, 2026-10-26). Always visible, so the rep sees which
 * items carry a note. Hovering the tag (not the row) shows the note after a
 * moment; a click, Enter or Space edits it. Same on macOS and WPF.
 */
export const PlaybookNoteTag: React.FC<PlaybookNoteTagProps> = ({ note, onEdit }) => {
  const [isCardShown, setIsCardShown] = useState(false);
  const showTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(showTimer.current), []);

  const showSoon = () => {
    window.clearTimeout(showTimer.current);
    showTimer.current = window.setTimeout(() => setIsCardShown(true), NOTE_CARD_DELAY_MS);
  };
  const hide = () => {
    window.clearTimeout(showTimer.current);
    setIsCardShown(false);
  };

  return (
    <>
      <button
        type="button"
        aria-label={onEdit ? `Edit note: ${note}` : `Note: ${note}`}
        aria-disabled={onEdit ? undefined : true}
        onClick={onEdit}
        onMouseEnter={showSoon}
        onMouseLeave={hide}
        onFocus={() => setIsCardShown(true)}
        onBlur={hide}
        className={`ml-1 mt-px inline-flex shrink-0 items-center rounded-inner bg-signature-tint px-1.5 py-px text-[10px] font-medium leading-3 text-signature focus-visible:outline focus-visible:outline-2 focus-visible:outline-signature ${onEdit ? '' : 'cursor-default'}`}
      >
        Note
      </button>
      {isCardShown && <PlaybookNoteHoverCard note={note} />}
    </>
  );
};

interface PlaybookNoteAffordanceProps {
  isChecked: boolean;
  /** The item's saved note, if any. */
  note?: string;
  /** True while the item's editor is open or its note is just arriving (`PlaybookItemNote`). */
  isHidden: boolean;
  /** Opens the item's note editor; absent while the checklist is read only. */
  onEdit?: () => void;
}

/**
 * What follows a checked item's text: its "Note" tag when it has a note, else
 * (when the rep can edit) the hover pencil that adds one. An unchecked item
 * shows neither, and both stay hidden while the editor is open or the note is
 * just arriving.
 */
export const PlaybookNoteAffordance: React.FC<PlaybookNoteAffordanceProps> = ({ isChecked, note, isHidden, onEdit }) => {
  if (!isChecked || isHidden) return null;
  if (note?.trim()) return <PlaybookNoteTag note={note} onEdit={onEdit} />;
  return onEdit ? <PlaybookNoteButton onClick={onEdit} /> : null;
};

interface PlaybookNoteEditorProps {
  value: string;
  /** The note as saved, to tell whether the rep changed it. */
  savedNote?: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

/**
 * A note's inline editor, which saves itself: Enter or a click anywhere else
 * keeps what is in the box (an emptied box removes the note), an unchanged
 * note just closes, and Escape cancels (owner, 2026-10-23). No Save button.
 * Same on macOS and WPF.
 */
export const PlaybookNoteEditor: React.FC<PlaybookNoteEditorProps> = ({ value, savedNote, onChange, onSave, onCancel }) => {
  const editor = useRef<HTMLDivElement>(null);
  const latest = useRef({ value, savedNote, onSave, onCancel });
  latest.current = { value, savedNote, onSave, onCancel };

  const commit = () => {
    const { value: text, savedNote: saved, onSave: save, onCancel: close } = latest.current;
    if (text.trim() !== (saved ?? '').trim()) save();
    else close();
  };

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!editor.current?.contains(event.target as Node)) commit();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
    // commit reads the latest props through the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={editor} className="mt-1">
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Add note..."
        aria-label="Note"
        className="w-full rounded-control border border-ink-border px-2 py-1 text-[11px] focus:border-signature focus:ring-1 focus:ring-signature"
        autoFocus
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') onCancel();
        }}
      />
    </div>
  );
};

export default PlaybookItemNote;
